
import React, { useEffect, useState } from "react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"; 
import { Button } from "@/components/ui/button";
import { Moon, Sun } from "lucide-react";
import { toast } from "@/hooks/use-toast";

export const ThemeToggle = () => {
  const [currentTheme, setCurrentTheme] = useState<string>("system");
  
  useEffect(() => {
    // Check if user previously set a theme
    const storedTheme = localStorage.getItem("theme") || "system";
    setCurrentTheme(storedTheme);
    
    applyTheme(storedTheme);
  }, []);

  const applyTheme = (theme: string) => {
    const root = document.documentElement;
    
    // First handle dark/light mode
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const isDark = theme === "dark" || (theme === "system" && prefersDark);
    
    // Remove dark class first
    root.classList.remove("dark");
    
    // Add dark class if needed
    if (isDark) {
      root.classList.add("dark");
    }
    
    localStorage.setItem("theme", theme);
    
    // Show toast for theme change
    if (theme !== "system") {
      toast({
        title: "Theme changed",
        description: `Theme set to ${theme}`,
        duration: 2000
      });
    }
  };

  const setTheme = (theme: string) => {
    setCurrentTheme(theme);
    applyTheme(theme);
  };

  // Find current theme icon to display
  const currentThemeIcon = currentTheme === "dark" || 
                          (currentTheme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches) 
                          ? <Moon className="h-4 w-4" /> 
                          : <Sun className="h-4 w-4" />;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" className="border-blue-200 dark:border-gray-700">
          {currentThemeIcon}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white dark:bg-gray-800">
        <DropdownMenuItem
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => setTheme("light")}
        >
          <div className="w-4 h-4 rounded-full bg-white border border-gray-200"></div>
          <span>Light</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => setTheme("dark")}
        >
          <div className="w-4 h-4 rounded-full bg-gray-700"></div>
          <span>Dark</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => setTheme("system")}
        >
          <div className="w-4 h-4 rounded-full bg-gradient-to-r from-gray-300 to-gray-500 dark:from-gray-700 dark:to-gray-900"></div>
          <span>System</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
